import { IsAllow, PlanTypes } from '../../../../redux-store/types/portfolio';
import { DEFAULT_MAX_INVESTMENT, EIGHTEEN_YEARS_IN_MILLISECONDS } from '../../../../utils/constants';
import {
    checkIsValidObject,
    dateConvert,
    getOccupation,
    gettaxStatusPersonal,
    isValidTaxDetail,
} from '../../portfolios/utils';
import { ErrorSectionTypes, NewPurchasePayload, ValidationErrorRes } from './types';
import en from '../../../../lang/en-us';
import { formatCurrency } from '../../../../utils/currency';
import { InvestThrough, MAX_LENGTHS, MIN_LENGHTHS } from '../Switch/types';
import { ChequeFile, PennyDropStatus } from '../../../../redux-store/reducers/newPurchase';

const {
    dbCommercial: { newPurchase, exploreMfs },
} = en;

type EntraInfoType = {
    plan?: string;
    investingThrough?: InvestThrough | null;
    euinCheckFlag?: IsAllow | null;

    chequeFile?: ChequeFile;
    pennyDropStatus?: PennyDropStatus;
};

const validateInvestThrough = (
    { schemeOptions }: NewPurchasePayload,
    extraInfo?: any,
): ValidationErrorRes => {
    const [schemeOption] = schemeOptions;
    const { plan, investingThrough, euinCheckFlag } = extraInfo;
    const { distId, euin, euinDeclarationFlag, riaCode } = schemeOption;

    if (!plan && !investingThrough && !euinCheckFlag) {
        return { error: false };
    }

    if ([PlanTypes.Reggular, PlanTypes.R].includes(plan)) {
        let validArnData =
            !!distId &&
            distId.slice(4).length >= MIN_LENGHTHS.ARN_LENGTH &&
            distId.slice(4).length <= MAX_LENGTHS.ARN_LENGTH;
        if (!validArnData) {
            return { message: exploreMfs.errors.enterDistSubDist, error: true };
        }
        if (
            !euinCheckFlag ||
            (euinCheckFlag === IsAllow.No && euinDeclarationFlag !== IsAllow.YES)
        ) {
            return { message: exploreMfs.errors.euinDeclaration, error: true };
        }
        if (euinCheckFlag === IsAllow.YES && (!euin || Number(euin) < MAX_LENGTHS.EUIN_LENGTH)) {
            return { message: exploreMfs.errors.EUINmandatory, error: true };
        }
    }
    if ([PlanTypes.D, PlanTypes.Direct].includes(plan)) {
        if (investingThrough === InvestThrough.ria && !riaCode) {
            return { message: exploreMfs.errors.RIAMandatory, error: true };
        }
    }
    return { error: false };
};
const validateIsin = ({ schemeOptions }: NewPurchasePayload): ValidationErrorRes => {
    const [schemeOption] = schemeOptions;
    const { isin } = schemeOption;
    if (!isin) {
        return { message: exploreMfs.errors.ISINMandatory, error: true };
    }
    return { error: false };
};
const validatePersonalDetials = ({ investorDetails }: NewPurchasePayload): ValidationErrorRes => {
    const {
        investorName,
        dob,
        occupation,
        taxStatus,
        email,
        mobile,
        mobileRelationship,
        emailRelationship,
    } = investorDetails;
    if (!Object.keys(gettaxStatusPersonal).includes(taxStatus)) {
        const message = exploreMfs.errors.taxStatusError;
        return { message, error: true, section: ErrorSectionTypes.PersonalDetails };
    }
    if (!Object.keys(getOccupation).includes(occupation)) {
        const message = exploreMfs.errors.NameDOBOccupationRequired;
        return { message, error: true, section: ErrorSectionTypes.PersonalDetails };
    }
    if (!checkIsValidObject({ investorName, dob, occupation, email, mobile })) {
        const message = exploreMfs.errors.NameDOBOccupationRequired;
        return { message, error: true, section: ErrorSectionTypes.PersonalDetails };
    }
    if (!checkIsValidObject({ emailRelationship, mobileRelationship })) {
        const message = exploreMfs.errors.relationshipError;
        return { message, error: true, section: ErrorSectionTypes.PersonalDetails };
    }
    return { error: false };
};

export function shouldUploadChequeButDidNot({ chequeFile, pennyDropStatus }: EntraInfoType) {
    return pennyDropStatus?.nameMatchStatus == 'N' && !chequeFile;
}

const validateBankDetails = (
    { investorDetails: { bank } }: NewPurchasePayload,
    extraInfo?: EntraInfoType | null,
): ValidationErrorRes => {
    const { accountNo, accountType, branch, ifsc, name: BankName } = bank;
    if (!checkIsValidObject({ accountNo, accountType, branch, ifsc, BankName })) {
        return {
            message: exploreMfs.errors.BankDetailsMandatory,
            error: true,
            section: ErrorSectionTypes.BankDetails,
        };
    }
    // console.log('bank validation ==== ',extraInfo)
    if (extraInfo) {
        if (shouldUploadChequeButDidNot(extraInfo)) {
            return {
                message: exploreMfs.errors.chequeRequired,
                error: true,
                section: ErrorSectionTypes.BankDetails,
            };
        }
    }
    return { error: false };
};
const validateFatcaDetails = ({
    investorDetails: { fatcaDetails },
}: NewPurchasePayload): ValidationErrorRes => {
    const factaDetailsByHolder = fatcaDetails.find((fatca) => fatca.fatcaId === 'FH');
    const fatcaResidencyOtherthanIndia =
        factaDetailsByHolder?.taxResidencyOtherthanIndia || factaDetailsByHolder?.declaration;
    if (
        fatcaResidencyOtherthanIndia !== IsAllow.No &&
        !factaDetailsByHolder?.taxResidency?.[0].tinReason &&
        !isValidTaxDetail(factaDetailsByHolder?.taxResidency?.[0])
    ) {
        return {
            message: exploreMfs.errors.fillAllDetails,
            error: true,
            section: ErrorSectionTypes.FatcaDetails,
        };
    }
    if (
        fatcaResidencyOtherthanIndia === IsAllow.YES &&
        (!factaDetailsByHolder?.placeOfBirth ||
            !factaDetailsByHolder?.countryOfBirth ||
            !factaDetailsByHolder?.nationality)
    ) {
        return {
            message: exploreMfs.errors.fatcaDetails,
            error: true,
            section: ErrorSectionTypes.FatcaDetails,
        };
    }
    if (fatcaResidencyOtherthanIndia === IsAllow.No && !factaDetailsByHolder?.placeOfBirth) {
        return {
            message: exploreMfs.errors.placeOfBirth,
            error: true,
            section: ErrorSectionTypes.FatcaDetails,
        };
    }
    if (
        // !factaDetailsByHolder?.fatherName ||
        // !factaDetailsByHolder?.spouseName ||
        !factaDetailsByHolder?.pepFlag ||
        !factaDetailsByHolder?.addressType ||
        !factaDetailsByHolder?.sourceOfWealth ||
        !factaDetailsByHolder?.annualIncomeSlab ||
        !factaDetailsByHolder?.netWorthDate
    ) {
        return {
            message: exploreMfs.errors.kycError,
            error: true,
            section: ErrorSectionTypes.KycDetails,
        };
    }
    if (Number(factaDetailsByHolder?.netWorth) <= 0) {
        return {
            message: exploreMfs.errors.zeroNetWorth,
            error: true,
            section: ErrorSectionTypes.KycDetails,
        };
    }
    return { error: false };
};
export const validateNomineeDetails = ({
    investorDetails: { nominee, nomineeFlag = true },
}: NewPurchasePayload): ValidationErrorRes => {
    if (!!nominee.length && !nomineeFlag) {
        const sharePercentage = nominee.reduce((prev: number, curr: any) => {
            const percentage = Number(curr.percentage);
            return prev + (isNaN(percentage) ? 0 : percentage);
        }, 0);
        if (sharePercentage / 100 !== 1) {
            return {
                message: exploreMfs.errors.nomineeShare100,
                error: true,
                section: ErrorSectionTypes.NomineeDetails,
            };
        }

        const emptyNomineeIndex = nominee.findIndex(
            (ele) => !ele.relation || !ele.name || !ele.percentage,
        );

        if (emptyNomineeIndex !== -1) {
            return {
                message: `Name, Relation, Share are required`,
                error: true,
                section: ErrorSectionTypes.NomineeDetails,
                index: emptyNomineeIndex,
            };
        }

        const invalidGuradianDetails = nominee.map((nominee, index) => {
            const nomineeDob = nominee.dob;
            const aboveEighteen: boolean = !!nomineeDob
                ? new Date().getTime() - new Date(nomineeDob).getTime() >
                  EIGHTEEN_YEARS_IN_MILLISECONDS
                : false;
            if (!aboveEighteen && !nominee.nomineeGuardian?.name) {
                return {
                    message: `Please enter guardian details`,
                    error: true,
                    section: ErrorSectionTypes.NomineeDetails,
                    index,
                };
            }
        });

    } else if (!nominee.length && !nomineeFlag) {
        return {
            message: exploreMfs.errors.noNominee,
            error: true,
            section: ErrorSectionTypes.NomineeDetails,
        };
    }
    return { error: false };
};

export const NewPurchaseValidations = [
    validateInvestThrough,
    validateIsin,
    validateNomineeDetails,
    validatePersonalDetials,
    validateFatcaDetails,
    validateBankDetails,
];
export const AdditionalPurchaseValidations = [validateInvestThrough, validateIsin];

export const showHintTemplateForMaxAndMin = ({ max, min = 1, multiples = 1 }: any) => {
    let maxComputedValue = `₹${formatCurrency(
        Number(!max || max === 'null' ? DEFAULT_MAX_INVESTMENT : max),
    )}`;
    let minComputedValue = `₹${formatCurrency(Number(min))}`;

    return `${newPurchase.validations.lumpsumError.minInvest}${minComputedValue} (${newPurchase.validations.lumpsumError.multipleOf}₹${multiples})`;
};

export const otmSipDateConditionNotValid = (
    omStartDate: string,
    omExpiryDate: string,
    fromDate: string | Date,
    toDate: string | Date,
) => {
    const fromDateValid =
        new Date(dateConvert(fromDate)) >= new Date(dateConvert(omStartDate)) &&
        new Date(dateConvert(fromDate)) <= new Date(dateConvert(omExpiryDate));
    const toDateValid =
        new Date(dateConvert(toDate)) >= new Date(dateConvert(omStartDate)) &&
        new Date(dateConvert(toDate)) <= new Date(dateConvert(omExpiryDate));
    return !(fromDateValid && toDateValid);
};
